import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';

import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import GoalsGridItemComponent from 'webapp/components/goals/grid-item';
import GoalsMyBaseComponent, { type GoalsMyArgs } from 'webapp/components/goals/my/base';

import type { Goal } from 'webapp/models-custom';
import FilterSortGoalMyInGrid from 'webapp/models-custom/filter-sort/goal-my-in-grid';

export default class GoalsMyGridComponent extends GoalsMyBaseComponent {
	filterSort: FilterSortGoalMyInGrid;

	constructor(owner: Owner, args: GoalsMyArgs) {
		super(owner, args);

		const filterSort = new FilterSortGoalMyInGrid({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.sortedModel;
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
			afterFilter: () => {
				this.doVisibleCheck();
			},
		});

		this.filterSort = filterSort;
	}

	@action
	override setupView(node: HTMLElement, sortingFavorites: boolean): void {
		super.setupView(node);

		const itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = (/*currentItem*/) => {
				return $(
					'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
				);
			};
		this.initSortable($(node), itemSelectorString, placeholderElementFn, sortingFavorites);
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId: string, sectionSelector: string): boolean => {
				const goal = this.mmGoalsService.goalForSpaceModels.find(
					(goal: Goal) => goal.id === itemId,
				);
				if (!goal) {
					return false;
				}
				return (
					(goal.favorite && sectionSelector === '.favorites-section') ||
					(!goal.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		{{! Favorites section }}
		{{#if this.hasFavoriteGoals}}
			<CommonListLazyLoadComponent
				@classNames="lazy-grid"
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.favoriteGoalsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<ul
					class="flex-ct flex-ct--size-goal favorites-section"
					data-hidden={{if this.filterSort.noResults "true" "false"}}
					{{didInsert this.setupFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{willDestroy this.teardownView}}
				>
					{{#each visibleItems as |item|}}
						<GoalsGridItemComponent
							@model={{item}}
							@batchSelect={{this.batchSelect}}
							@hideSort={{exporter.hideSort}}
						/>
					{{/each}}
					{{#each (makeArray length=exporter.itemsPerRow)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>
				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>
		{{/if}}

		{{! Non-favorites section }}
		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@filterExtraFn={{this.nonFavoriteGoalsFilterFn}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
			as |visibleItems exporter|
		>
			<ul
				class="flex-ct flex-ct--size-goal non-favorites-section"
				data-hidden={{if this.filterSort.noResults "true" "false"}}
				{{didInsert this.setupNonFavoritesView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |item|}}
					<GoalsGridItemComponent
						@model={{item}}
						@batchSelect={{this.batchSelect}}
						@hideSort={{exporter.hideSort}}
					/>
				{{/each}}
				{{#if exporter.areAllItemsLoaded}}
					<li
						role="button"
						class="card card--placeholder-new flex-item flex-item--placeholder filter-all"
						{{on "click" @onAdd}}
					>
						<div class="default">
							<CommonUiIconComponent @name="Plus" @size="xl" />
							New goal
						</div>
					</li>
				{{/if}}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>
			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text={{this.addButtonText}} @callTrigger={{@onAdd}} />
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
